import { useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetLmsCreateRequestsQuery, useAddLmsCreateRequestMutation, useUpdateLmsCreateRequestMutation, useDeleteLmsCreateRequestMutation, useChangeLmsCreateRequestStatusMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

// "Tüm Talepler" sekmesi için AYRI bir veri hook'u — hooks/useTrainingRequestData.js'in kopyası
// değil, bilinçli olarak ondan bağımsız (bkz. docs/talep_olustur_onay_akısı.md). Sebep: bu sekme
// (a) `RequestType`'a göre SABİT bir filtre uygulamıyor (her iki türü de gösteriyor) ve (b)
// `status`/`requestType` alanlarını serbest metin yerine kesin eşleşme (dropdown, EQUALS) filtresi
// olarak sunuyor. `useTrainingRequestData` paylaşımlı olduğu ve mevcut iki sekme (Yayınlanan/Talep
// Edilen) tarafından da kullanıldığı için, oradaki filtre grupları DEĞİŞTİRİLMEDİ — bu yeni
// sekmenin ihtiyaçları kendi hook'unda karşılanıyor.
const BACKEND_COLUMN_MAP = {
    vehicleModel: 'VehicleModel',
    trainingType: 'TrainingType',
    numberOfParticipants: 'NumberOfParticipants',
    preferTrainingDate: 'PreferTrainingDate',
    alternativeDate: 'AlternativeDate',
    location: 'LocationCode',
    trainingLanguage: 'TrainingLanguage',
    status: 'Status',
    requestType: 'RequestType',
    createdDate: 'CreatedDate',
    createdName: 'CreatedName'
};

// `status`/`requestType` burada kesin eşleşme (dropdown, EQUALS) grubunda — bkz.
// docs/talep_olustur_onay_akısı.md "STATÜ FİLTRESİ" ({ column: 'Status', operator: 5, value }).
const EXACT_FILTER_FIELDS = ['vehicleModel', 'trainingType', 'location', 'status', 'requestType'];
const TEXT_FILTER_FIELDS = ['numberOfParticipants', 'trainingLanguage', 'createdName'];
const DATE_FILTER_FIELDS = ['preferTrainingDate', 'alternativeDate', 'createdDate'];

const INITIAL_FILTER_STATE = {
    vehicleModel: null,
    trainingType: null,
    location: null,
    status: null,
    requestType: null,
    numberOfParticipants: null,
    numberOfParticipantsMatchMode: null,
    trainingLanguage: null,
    trainingLanguageMatchMode: null,
    createdName: null,
    createdNameMatchMode: null,
    preferTrainingDate: null,
    alternativeDate: null,
    createdDate: null
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];
    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;
        const column = BACKEND_COLUMN_MAP[field];
        if (!column) return;

        let filterValue;
        if (DATE_FILTER_FIELDS.includes(field) && value instanceof Date) {
            filterValue = value.toISOString();
        } else {
            filterValue = String(value).trim();
            if (!filterValue) return;
        }

        filters.push({ column, operator: matchModeToOperator[matchMode] ?? matchModeToOperator[FilterMatchMode.EQUALS], value: filterValue });
    };

    EXACT_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], FilterMatchMode.EQUALS));
    TEXT_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], filtersState?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS));
    DATE_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], FilterMatchMode.DATE_IS));

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || !order) return [{ column: 'CreatedDate', direction: 2 }];
    const column = BACKEND_COLUMN_MAP[field] || field;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

/**
 * "Tüm Talepler" sekmesi (onay akışı) için CRUD + statü değişikliği veri/state hook'u. Bkz.
 * docs/talep_olustur_onay_akısı.md. Liste, her iki `requestType`'ı da (Yayınlanan/Talep Edilen)
 * herhangi bir sabit filtre uygulamadan döner; kullanıcı isterse `status`/`requestType` kolon
 * filtreleriyle daraltabilir.
 */
const useAllRequestsData = () => {
    const { th } = useLanguage();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const queryParams = useMemo(
        () => ({
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: buildDynamicFilters(activeFilters),
            sorts: buildDynamicSorts(sortField, sortOrder)
        }),
        [lazyParams, activeFilters, sortField, sortOrder]
    );

    const { data: trainingRequestApi, isLoading, isFetching, error, refetch } = useGetLmsCreateRequestsQuery(queryParams, { refetchOnMountOrArgChange: true });

    const [addMutation] = useAddLmsCreateRequestMutation();
    const [updateMutation] = useUpdateLmsCreateRequestMutation();
    const [deleteMutation] = useDeleteLmsCreateRequestMutation();
    const [changeStatusMutation] = useChangeLmsCreateRequestStatusMutation();

    const saveRequest = async (formData) => {
        try {
            if (formData.id) {
                await updateMutation(formData).unwrap();
            } else {
                await addMutation(formData).unwrap();
            }
            toastService.success(th('trainingRequest.saveSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('trainingRequest.saveError'), th('common.error'));
            return false;
        }
    };

    const deleteRequest = async (id) => {
        await deleteMutation(id).unwrap();
        refetch?.();
    };

    // Statü geçişi (Onaya Gönder / Kabul / Red / İade) — bkz.
    // docs/talep_olustur_onay_akısı.md "STATÜ DEĞİŞTİRME". Hata durumunda çağıran taraf (bkz.
    // AllRequestsTab.jsx) kendi toast/telafi mantığını uygulasın diye burada yutulmuyor, olduğu
    // gibi fırlatılıyor.
    const changeStatus = async ({ id, newStatus, comment, requestType }) => {
        await changeStatusMutation({ id, newStatus, comment, requestType }).unwrap();
        refetch?.();
    };

    const onPage = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            page: event.page + 1,
            first: event.first,
            rows: event.rows
        }));
    };

    const onFiltersChange = (updated) => {
        setActiveFilters(updated);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const onSortChange = (field, order) => {
        setSortField(field);
        setSortOrder(order);
    };

    return {
        trainingRequestApi: trainingRequestApi || { items: [], totalCount: 0 },
        isLoading,
        isFetching,
        error,
        lazyParams,
        onPage,
        activeFilters,
        onFiltersChange,
        initFilters,
        sortField,
        sortOrder,
        onSortChange,
        saveRequest,
        deleteRequest,
        changeStatus,
        refetch
    };
};

export default useAllRequestsData;
